<template>
  <v-menu
    v-if="loggedIn"
    v-model="menu"
    transition="scale-transition"
    location="bottom"
    offset="6">
    <template #activator="{ props }">
      <v-list
        v-bind="props"
        class="user-menu-activator rounded-xl"
        :class="{ 'bg-background-gray': menu }">
        <v-list-item
          :title="currentUser.name"
          :subtitle="
            currentAppClient
              ? currentAppClient.name
              : currentOrg
                ? currentOrg.name
                : ''
          ">
          <template #prepend>
            <v-avatar color="primary">
              <span class="text-h6">
                {{ userInitials }}
              </span>
            </v-avatar>
          </template>
          <template #append>
            <v-icon v-if="menu">mdi-chevron-up</v-icon>
            <v-icon v-else>mdi-chevron-down</v-icon>
          </template>
        </v-list-item>
      </v-list>
    </template>
    <v-sheet min-width="200" rounded="lg">
      <v-list class="menu-list mt-1 px-1" density="compact">
        <v-list-item
          v-for="(menuItem, i) in menuItems"
          :key="i"
          @click="menuItem.action ? menuItem.action() : navigate(menuItem.to)">
          <template #prepend>
            <v-icon size="small" :icon="menuItem.icon" />
          </template>
          <template #title>
            {{ menuItem.text }}
          </template>
        </v-list-item>
        <template v-if="showOrgSwitcher">
          <v-divider class="my-2" />
          <v-list-item class="org-switcher-heading">
            Switch Organization
          </v-list-item>
          <v-list-item
            v-for="item in userOrgsAndAppClients"
            :key="`${item.objectType}-${item.id}`"
            rounded="lg"
            :title="item.name"
            :active="isCurrentSelection(item)"
            :disabled="isCurrentSelection(item)"
            @click="switchOrgOrAppClient(item)">
            <template #append>
              <v-icon
                v-if="isCurrentSelection(item)"
                size="small"
                icon="mdi-check" />
            </template>
          </v-list-item>
        </template>
      </v-list>
    </v-sheet>
  </v-menu>
</template>

<script>
import { userTierMixin } from "@/mixins/UserTier";
import { mapStores } from "pinia";
import { useAppStateStore } from "@/stores/AppStateStore";
import { useAudienceExplorerSearchStore } from "@/stores/AudienceExplorerSearchStore";
import { useCampaignEffectivenessStore } from "@/stores/CampaignEffectivenessStore";
import { useFavoriteStore } from "@/stores/FavoriteStore";
import { useFileStore } from "@/stores/FileStore";
import { useMetaAudienceStore } from "@/stores/MetaAudienceStore";
import { useResearchStudioStore } from "@/stores/ResearchStudioStore";
import { useUserStore } from "@/stores/UserStore";
import { useUserActivityStore } from "@/stores/UserActivityStore";

export default {
  name: "UserMenu",
  mixins: [userTierMixin],
  props: {
    impersonating: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["stopImpersonating"],
  data: (vm) => ({
    menu: false,
    userMenuItems: [
      { text: "Account", icon: "mdi-account-outline", to: "/account" },
      {
        text: "File Sharing",
        icon: "mdi-file-lock-outline",
        to: "/file-sharing",
      },
      {
        text: "Settings",
        icon: "mdi-cog-outline",
        to: "/settings",
        permissions: [
          "manage_orgs",
          "manage_users",
          "manage_org_users",
          "manage_external_users",
          "manage_roles",
          "manage_tableau_workbooks",
          "manage_clients",
          "manage_pricing_tiers",
        ],
      },
      {
        text: "Logout",
        icon: "mdi-logout",
        action: () => {
          vm.appStateStore.breadcrumbs = [];
          if (vm.impersonating) {
            vm.menu = false;
            vm.$emit("stopImpersonating");
          } else {
            vm.$router.push({ path: "/logout" }).catch(() => {});
          }
        },
      },
    ],
  }),
  computed: {
    ...mapStores(
      useAppStateStore,
      useAudienceExplorerSearchStore,
      useCampaignEffectivenessStore,
      useFavoriteStore,
      useFileStore,
      useMetaAudienceStore,
      useResearchStudioStore,
      useUserStore,
      useUserActivityStore,
    ),
    userInitials() {
      return this.currentUser.name
        .match(/(\b\S)?/g)
        .join("")
        .match(/(^\S|\S$)?/g)
        .join("")
        .toUpperCase();
    },
    menuItems() {
      return this.userMenuItems.filter((item) => this.authorized(item));
    },
    showOrgSwitcher() {
      return this.userOrgsAndAppClients.length > 1;
    },
    activeSelection() {
      // org_id and app_client_id are stored in independent cookies, so both
      // currentOrg and currentAppClient can be set at once. Collapse them into
      // a single active selection (org takes precedence, matching
      // switchOrgOrAppClient) so org/app clients that share a numeric id can't
      // both appear active.
      if (this.currentOrg) {
        return { objectType: "org", id: this.currentOrg.id };
      }
      if (this.currentAppClient) {
        return { objectType: "app_client", id: this.currentAppClient.id };
      }
      return null;
    },
    userOrgsAndAppClients() {
      // Prefer app clients over same-named orgs (post-migration). UserStore
      // migrates colliding org cookies/defaults to the app client, so hiding
      // the org here keeps the switcher and active selection in sync.
      const appClients = [
        ...(this.userStore.currentUser.app_clients ?? []),
      ].map((client) => ({ ...client, objectType: "app_client" }));
      const appClientNames = new Set(appClients.map((client) => client.name));
      const orgs = [...this.userStore.currentUser.orgs]
        .filter((org) => !appClientNames.has(org.name))
        .map((org) => ({ ...org, objectType: "org" }));
      return [...orgs, ...appClients].sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    },
  },
  methods: {
    authorized(menuItem) {
      if (!menuItem.permissions) return true;
      return menuItem.permissions.some(
        (p) => this.$can(p) || this.$hasProduct(p),
      );
    },
    navigate(to) {
      this.appStateStore.breadcrumbs = [];
      this.$router.push(to);
    },
    isCurrentSelection(item) {
      return (
        this.activeSelection?.objectType === item.objectType &&
        this.activeSelection?.id === item.id
      );
    },
    switchOrgOrAppClient(item) {
      const current = this.currentOrg || this.currentAppClient;
      const currentType = this.currentOrg ? "org" : "app_client";

      if (currentType === item.objectType && current?.id === item.id) return;

      if (
        JSON.stringify(this.appStateStore.breadcrumbs) !==
        JSON.stringify(["Audiences", "Audience Explorer"])
      ) {
        this.appStateStore.breadcrumbs = [];
      }

      this.userActivityStore.create({
        event: "org switch",
        meta_data: {
          current_org: this.currentOrg
            ? { id: this.currentOrg.id, name: this.currentOrg.name }
            : undefined,
          current_app_client: this.currentAppClient
            ? { id: this.currentAppClient.id, name: this.currentAppClient.name }
            : undefined,
          new_org:
            item.objectType === "org"
              ? { id: item.id, name: item.name }
              : undefined,
          new_app_client:
            item.objectType === "app_client"
              ? { id: item.id, name: item.name }
              : undefined,
        },
      });

      if (item.objectType === "org") {
        this.userStore.setCurrentAppClient(undefined);
        this.switchOrg(item);
      } else {
        this.userStore.setCurrentOrg(undefined);
        this.switchAppClient(item);
      }
    },
    switchOrg(org) {
      this.userStore.setCurrentOrg(org.id);
      this.$router
        .push({
          name: "home",
          params: {
            meta_audience_id:
              this.audienceExplorerSearchStore.defaultMetaAudienceId,
            geography_id: 1,
          },
          query: { refresh: true },
        })
        .catch(() => {})
        .finally(() => {
          this.userStore.getCurrentUser();
          this.metaAudienceStore.getMetaAudiences();
          this.userActivityStore.setRecentSearches([]);
          this.campaignEffectivenessStore.listCampaigns();
          this.researchStudioStore.listSurveys();
          this.filesStore.clearFiles();
          this.favoriteStore.setUserBookmarks({
            type: "audience-builder",
            data: [],
          });
          this.favoriteStore.setUserBookmarks({
            type: "tv-rating",
            data: [],
          });
        });
    },
    switchAppClient(client) {
      this.userStore.setCurrentAppClient(client.id);
      this.$router
        .push({
          name: "home",
          params: {
            meta_audience_id:
              this.audienceExplorerSearchStore.defaultMetaAudienceId,
            geography_id: 1,
          },
          query: { refresh: true },
        })
        .catch(() => {})
        .finally(() => {
          this.userStore.getCurrentUser();
          this.userActivityStore.setRecentSearches([]);
          this.campaignEffectivenessStore.listCampaigns();
          this.researchStudioStore.listSurveys();
          this.filesStore.clearFiles();
          this.favoriteStore.setUserBookmarks({
            type: "audience-builder",
            data: [],
          });
          this.favoriteStore.setUserBookmarks({
            type: "tv-rating",
            data: [],
          });
        });
    },
  },
};
</script>

<style scoped lang="scss">
.user-menu-activator {
  font-family: Barlow, sans-serif;
  line-height: 20px;
  letter-spacing: 0.1px;
  padding: 4px;
  margin-right: 8px;
  cursor: pointer;
  color: rgb(var(--v-theme-prominent));
  :deep(.v-list-item__append) {
    width: 36px;
  }
  :deep(.v-list-item-title) {
    font-size: 16px;
    font-weight: 600;
  }
  :deep(.v-list-item-subtitle) {
    font-family: Barlow, sans-serif;
    font-size: 14px;
    font-weight: 400;
  }
}
.menu-list {
  font-family: Barlow, sans-serif;
  color: rgb(var(--v-theme-prominent));
  :deep(.v-list-item__prepend) {
    width: 28px;
  }
  :deep(.v-list-item-title) {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.1px;
  }
  .org-switcher-heading {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: 0.1px;
  }
}
</style>
